import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import InsuranceRepImport from '../components/InsuranceRepImport';
import { Modal } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import InsuranceRepForm from '../components/forms/InsuranceRepForm';

interface InsuranceRepresentative {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  title?: string;
  status: string;
  notes?: string;
  insurerId: string;
  tenantId: string;
  insurer: {
    id: string;
    name: string;
    code: string | null;
  };
}

interface Insurer {
  id: string;
  name: string;
  code: string | null;
}

interface Analytics {
  totalReps: number;
  recentReps: number;
  repsWithEmail: number;
  repsWithoutEmail: number;
  repsByInsurer: Array<{
    insurer: Insurer;
    count: number;
  }>;
  emailCompletionRate: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface ApiResponse {
  success: boolean;
  data: InsuranceRepresentative[];
  pagination: PaginationInfo;
}

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

const InsuranceRepresentatives: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedInsurer, setSelectedInsurer] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('lastName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [insurers, setInsurers] = useState<Insurer[]>([]);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRepresentative, setSelectedRepresentative] = useState<InsuranceRepresentative | null>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch insurers for filter dropdown
  const { data: insurersData } = useQuery({
    queryKey: ['insurers-dropdown'],
    queryFn: async (): Promise<{ success: boolean; data: Insurer[] }> => {
      const response = await api.get<{ success: boolean; data: Insurer[] }>('/insurers/dropdown');
      return response.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Fetch analytics
  const { data: analyticsData, isLoading: analyticsLoading, error: analyticsError } = useQuery({
    queryKey: ['insuranceRepsAnalytics'],
    queryFn: async (): Promise<{ success: boolean; data: Analytics }> => {
      const response = await api.get<{ success: boolean; data: Analytics }>('/insurance-reps/analytics');
      return response.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Fetch representatives with search, filtering, and pagination
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['insuranceRepresentatives', page, rowsPerPage, debouncedSearchTerm, selectedInsurer, sortBy, sortOrder],
    queryFn: async (): Promise<ApiResponse> => {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        sortBy,
        sortOrder,
      });

      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
      }

      if (selectedInsurer) {
        params.append('insurerId', selectedInsurer);
      }

      const response = await api.get<ApiResponse>(`/insurance-reps?${params.toString()}`);
      return response.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (insurersData?.success) {
      setInsurers(insurersData.data);
    }
  }, [insurersData]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleInsurerFilterChange = (event: SelectChangeEvent<string>) => {
    setSelectedInsurer(event.target.value);
    setPage(0);
  };

  const handleSortChange = (event: SelectChangeEvent<string>) => {
    setSortBy(event.target.value);
    setPage(0);
  };

  const handleSortOrderChange = (event: SelectChangeEvent<'asc' | 'desc'>) => {
    setSortOrder(event.target.value as 'asc' | 'desc');
    setPage(0);
  };

  const handleAdd = () => {
    console.log('Opening Add Representative modal...');
    setIsAddModalOpen(true);
  };

  const handleAddSuccess = () => {
    setIsAddModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['insuranceRepresentatives'] });
    queryClient.invalidateQueries({ queryKey: ['insuranceRepsAnalytics'] });
  };

  const handleAddCancel = () => {
    setIsAddModalOpen(false);
  };

  const analytics = analyticsData?.data;
  const representatives = data?.data || [];
  const pagination = data?.pagination;
  const isSearching = searchTerm !== debouncedSearchTerm;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/insurance-reps/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insuranceRepresentatives'] });
      queryClient.invalidateQueries({ queryKey: ['insuranceRepsAnalytics'] });
    },
  });

  const handleEdit = (id: string) => {
    const rep = representatives.find(r => r.id === id);
    if (rep) {
      setSelectedRepresentative(rep);
      setIsEditModalOpen(true);
    }
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    setSelectedRepresentative(null);
    queryClient.invalidateQueries({ queryKey: ['insuranceRepresentatives'] });
    queryClient.invalidateQueries({ queryKey: ['insuranceRepsAnalytics'] });
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
    setSelectedRepresentative(null);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this representative?')) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error) {
        console.error('Failed to delete representative:', error);
      }
    }
  };

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load insurance representatives: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => refetch()} 
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add Representative
        </Button>
      </Box>

      {/* Analytics Cards */}
      {analyticsLoading ? (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[1, 2, 3, 4].map((item) => (
            <Card key={item} sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 80 }}>
                  <CircularProgress size={30} />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : analyticsError ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Unable to load analytics data. Some features may be limited.
        </Alert>
      ) : analytics ? (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Representatives
                  </Typography>
                  <Typography variant="h5">
                    {analytics.totalReps}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Recent (30 days)
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {analytics.recentReps}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon color="info" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    With Email
                  </Typography>
                  <Typography variant="h5" color="info.main">
                    {analytics.repsWithEmail}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon color="warning" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Email Completion
                  </Typography>
                  <Typography variant="h5" color="warning.main">
                    {analytics.emailCompletionRate}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      ) : null}

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search representatives..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ flex: '1 1 300px', minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: isSearching && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ flex: '1 1 150px', minWidth: 150 }}>
            <InputLabel>Insurer</InputLabel>
            <Select
              value={selectedInsurer}
              label="Insurer"
              onChange={handleInsurerFilterChange}
            >
              <MenuItem value="">All Insurers</MenuItem>
              {insurers.map((insurer) => (
                <MenuItem key={insurer.id} value={insurer.id}>
                  {insurer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ flex: '1 1 150px', minWidth: 150 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              label="Sort By"
              onChange={handleSortChange}
            >
              <MenuItem value="lastName">Last Name</MenuItem>
              <MenuItem value="firstName">First Name</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="insurer">Company</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ flex: '1 1 150px', minWidth: 150 }}>
            <InputLabel>Order</InputLabel>
            <Select
              value={sortOrder}
              label="Order"
              onChange={handleSortOrderChange}
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        {/* Filter Status */}
        {(searchTerm || selectedInsurer) && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="textSecondary">
              Active filters:
            </Typography>
            {searchTerm && (
              <Chip 
                label={`Search: "${searchTerm}"`} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            )}
            {selectedInsurer && (
              <Chip 
                label={`Insurer: ${insurers.find(i => i.id === selectedInsurer)?.name || 'Unknown'}`} 
                size="small" 
                color="secondary" 
                variant="outlined"
              />
            )}
            <Button 
              size="small" 
              onClick={() => {
                setSearchTerm('');
                setSelectedInsurer('');
                setPage(0);
              }}
            >
              Clear All
            </Button>
          </Box>
        )}
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {representatives.length > 0 ? (
              representatives.map((rep) => (
                <TableRow key={rep.id}>
                  <TableCell>
                    <Button
                      variant="text"
                      onClick={() => navigate(`/insurance-representatives/${rep.id}`)}
                      sx={{ 
                        textTransform: 'none', 
                        p: 0, 
                        minWidth: 'auto',
                        color: 'primary.main',
                        '&:hover': {
                          textDecoration: 'underline',
                          backgroundColor: 'transparent'
                        }
                      }}
                    >
                      {`${rep.firstName} ${rep.lastName}`}
                    </Button>
                  </TableCell>
                  <TableCell>
                    {rep.email ? (
                      <Chip 
                        label={rep.email} 
                        size="small" 
                        color="success" 
                        variant="outlined"
                      />
                    ) : (
                      <Chip 
                        label="No Email" 
                        size="small" 
                        color="warning" 
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>{rep.phone || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={rep.insurer.name} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(rep.id)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(rep.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {searchTerm || selectedInsurer ? 'No representatives found matching your criteria' : 'No representatives found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {pagination && (
          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        )}
      </TableContainer>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          startIcon={<UploadOutlined />}
          onClick={() => setIsImportModalVisible(true)}
          variant="outlined"
        >
          Import CSV
        </Button>
      </Box>

      <Modal
        title="Import Insurance Representatives from CSV"
        open={isImportModalVisible}
        onCancel={() => setIsImportModalVisible(false)}
        footer={null}
        width={800}
        destroyOnHidden
      >
        <InsuranceRepImport />
      </Modal>

      <Dialog
        open={isAddModalOpen}
        onClose={handleAddCancel}
        maxWidth="md"
        fullWidth
        aria-labelledby="add-representative-dialog-title"
      >
        <DialogTitle id="add-representative-dialog-title">
          Add Insurance Representative
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <InsuranceRepForm onSuccess={handleAddSuccess} onCancel={handleAddCancel} />
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isEditModalOpen}
        onClose={handleEditCancel}
        maxWidth="md"
        fullWidth
        aria-labelledby="edit-representative-dialog-title"
      >
        <DialogTitle id="edit-representative-dialog-title">
          Edit Insurance Representative
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <InsuranceRepForm 
              insuranceRep={selectedRepresentative ? { ...selectedRepresentative, email: selectedRepresentative.email || '' } : undefined} 
              onSuccess={handleEditSuccess} 
              onCancel={handleEditCancel} 
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default InsuranceRepresentatives; 